import {
  PrivateOfferCreation,
  useFlyOutProductsBasedOnSelectedCloud,
  DetailedOffer,
} from '@labrav/marketplace'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ButtonV2,
  ModalConfirm,
  ModalSizeVariants,
  StepperComponent,
} from '@labrav/react-components'

import { useStyles } from './EditPrivateOfferWrapper.styles'
import { stepsForPrivateOffer } from './NewPrivateOfferWrapper'

import { useDispatch, useSelector } from '../../store/hooks'
import { debounce, isEmpty } from 'lodash'
import {
  addRowsOnPrivateOffer,
  clearPrivateOfferInCreation,
  deleteRowsOnPrivateOffer,
  getCurrentPrivateOfferInCreation,
  sendPrivateOffer,
  updatePrivateOfferInCreation,
  updateStandardDimensionsOnPrivateOffer,
  updatevaluesInArrayOfPrivateOffers,
} from '../../modules/privateOffer/privateOfferCreation/action'
import { getZeroDollarPricing } from '../../modules/privateOffer/privateOfferCreation/utils/dimensionHelper'
import type { KeyValue } from '../../modules/privateOffer/privateOfferCreation/types'
import { useNavigate, useParams } from 'react-router-dom'
import { Product } from '../../modules/productsListing/reducer'
import { getProducts } from '../../modules/productsListing/action'
import {
  bucketName,
  flyoutFormDebounceTime,
} from '../../common/utils/constants'
import { OffersWithErrors } from '../../modules/privateOffer/privateOfferCreation/reducer'
import { defaultNow } from '@labrav/common'
import { uploadFiles } from '../../modules/uploadEulaFiles/action'
import { Dimension, OfferStatus } from '../../modules/privateOffer/reducer'
import { usePrivateofferSendAndDraftDisabled } from '../../common/utils/hooks/privateOfferSentDisabled'
import { PrivateOfferAddEditFormik } from './PrivateOfferAddEditFormik/PrivateOfferAddEditFormik'
import { useAccountId } from '@labrav/common'
import { useSyncFlyoutRouteParams } from '../../common/utils/hooks/useSyncFlyoutRouteParams'
import { AccountDataSelection } from '@labrav/common'
import {
  LoadingTypes,
  isLoading,
  useCurrentCloudProvider,
} from '@labrav/common'

export const EditPrivateOfferWrapper: React.FC<{
  externalBaseURL?: string
  search?: string
  crmOpportunityId?: string
  crmObjectLink?: string
}> = ({ externalBaseURL, search, crmOpportunityId, crmObjectLink }) => {
  // Sync route params to selectedAccountIds for FLYOUT
  useSyncFlyoutRouteParams(!!externalBaseURL)

  const { classes } = useStyles()
  const navigate = useNavigate()

  const dispatch = useDispatch()

  const {
    cloud: routeCloud,
    accountId,
    productId = '',
    privateOfferId,
    partnerId,
  } = useParams<{
    cloud: string
    accountId: string
    productId: string
    privateOfferId: string
    partnerId?: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''

  // For external apps, if accountSelection doesn't have value, fallback to availableAccounts
  const availableAccounts = useSelector(
    state =>
      (state.accountSelection?.availableAccounts ||
        []) as AccountDataSelection[]
  )
  const externalAppFallbackAccountId = useMemo(() => {
    if (externalBaseURL && !accountId && !fallbackAccountId) {
      const flyoutAccounts = availableAccounts.filter(
        account => account?.product?.toLowerCase() === 'flyout'
      )
      // If routeCloud is available, match by cloud; otherwise use first available account
      if (routeCloud) {
        const accountForCloud = flyoutAccounts.find(
          account => account?.cloud?.toUpperCase() === routeCloud.toUpperCase()
        )
        return accountForCloud?.id || ''
      } else if (flyoutAccounts.length > 0) {
        // For external apps without cloud in route, use first available account
        return flyoutAccounts[0]?.id || ''
      }
    }
    return ''
  }, [
    externalBaseURL,
    accountId,
    fallbackAccountId,
    routeCloud,
    availableAccounts,
  ])

  // For external apps, use partnerId from URL as final fallback when routeAccountId is empty
  const routeAccountId =
    accountId ||
    fallbackAccountId ||
    externalAppFallbackAccountId ||
    (externalBaseURL && partnerId ? partnerId : '')

  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const product =
    products.find(product => product.productId === productId) || ({} as Product)

  const [openModal, setOpenModal] = useState<boolean>(false)
  const offer = useSelector<OffersWithErrors | undefined>(
    state =>
      state.privateOffersInCreation.currentPrivateOfferInCreation[productId]
  )

  const privateOffer = useMemo(() => {
    return offer || ({} as OffersWithErrors)
  }, [offer])

  const baseURL = useMemo(() => {
    if (externalBaseURL) return externalBaseURL
    if (routeCloud && routeAccountId) {
      return `/flyout/${routeCloud}/${routeAccountId}/products-listing`
    }
    return `/flyout/products-listing`
  }, [externalBaseURL, routeCloud, routeAccountId])
  useEffect(() => {
    if (privateOffer.hasBeenSent && !isEmpty(privateOffer.privateOfferId)) {
      const privateOfferId = privateOffer.privateOfferId
      // For external URLs, baseURL already contains productId
      const privatOfferDetailsPath = externalBaseURL
        ? `${baseURL}/private-offers/${privateOfferId}/details`
        : `${baseURL}/${productId}/private-offers/${privateOfferId}/details`
      navigate(
        {
          pathname: privatOfferDetailsPath,
          search,
        },
        { replace: true }
      )
      return () => {
        dispatch(clearPrivateOfferInCreation(productId))
      }
    } else if (
      !isEmpty(privateOffer.privateOfferId) &&
      privateOffer.noOfTimesSaved === 1
    ) {
      // For external URLs, baseURL already contains productId
      const privateOfferEditPath = `${baseURL}/${productId}/private-offers/${privateOffer.privateOfferId}/edit`
      navigate(
        {
          pathname: privateOfferEditPath,
          search,
        },
        { replace: true }
      )
    }
  }, [
    privateOffer.privateOfferId,
    privateOffer.hasBeenSent,
    privateOffer.noOfTimesSaved,
  ])
  useEffect(() => {
    if (!isEmpty(routeAccountId)) {
      {
        if (isEmpty(products)) {
          dispatch(
            getProducts(routeAccountId || '', selectedCloudTypeProductListing)
          )
        } else {
          dispatch(
            getCurrentPrivateOfferInCreation({
              productId,
              partnerId: routeAccountId || '',
              privateOfferId: privateOfferId || '',
              crmOpportunityId,
            })
          )
        }
      }
    }
  }, [routeAccountId, products, productId, privateOfferId, crmOpportunityId])

  //Need to fix will TODO:https://ibexlabs.atlassian.net/browse/LT-6567
  // useEffect(() => {
  //   return () => {
  //     dispatch(clearPrivateOfferInCreation(productId))
  //   }
  // }, [])

  const [activeStep, _] = useState<number>(0)

  const [selectedEulaFiles, setSelectedEulaFiles] = useState<File[]>([])

  const { sendDisabled, draftDisabled } = usePrivateofferSendAndDraftDisabled(
    productId,
    selectedEulaFiles
  )

  const handleChange = useCallback(
    debounce(async (data: KeyValue) => {
      await dispatch(
        updatePrivateOfferInCreation(
          {
            key: data.key,
            value: data.value,
          },
          productId
        )
      )
    }, flyoutFormDebounceTime),
    [productId]
  )
  const addRow = useCallback(
    async (key: string, type = 'standard') => {
      await dispatch(addRowsOnPrivateOffer(key, productId, type))
    },
    [productId]
  )

  const updateStandardDimensions = useCallback(
    async (index: number, dimension: Dimension) => {
      await dispatch(
        updateStandardDimensionsOnPrivateOffer(index, productId, dimension)
      )
    },
    [productId]
  )
  const deleteRow = useCallback(
    (index: number, key: string) => async () => {
      await dispatch(deleteRowsOnPrivateOffer(key, index, productId))
    },
    [productId]
  )
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const updateValuesInArray = useCallback(
    (index: number, key: string, type?: string) =>
      debounce(async (data: KeyValue) => {
        await dispatch(
          updatevaluesInArrayOfPrivateOffers(key, index, data, productId)
        )
      }, flyoutFormDebounceTime),
    [productId]
  )
  const setEulaFile = useCallback(
    (files: File[]) => {
      setSelectedEulaFiles(files)
    },
    [selectedEulaFiles, productId]
  )
  const disableUpload = useMemo(() => {
    if (
      (privateOffer?.eulaFile || []).length + selectedEulaFiles.length >= 5 ||
      loading
    ) {
      return true
    } else {
      return false
    }
  }, [selectedEulaFiles, privateOffer?.eulaFile, loading])

  const uploadToS3 = useCallback(
    async (saveAsDraft: boolean) => {
      await dispatch(
        uploadFiles({
          partnerId: routeAccountId || '',
          productId,
          filesToBeUploaded: selectedEulaFiles,
          assetType: 'eula_files',
          folder: 'private_offer_eula',
          defaultNow,
          saveAsDraft,
          crmOpportunityId,
          crmObjectLink,
          bucketName,
          showRealFileName: true,
        })
      )
      setSelectedEulaFiles([])
    },
    [privateOffer, routeAccountId, productId, selectedEulaFiles]
  )

  const handleSentToBuyer = useCallback(
    (saveAsDraft: boolean) => async () => {
      if (saveAsDraft) {
        setOpenModal(false)
      }
      if (!isEmpty(selectedEulaFiles)) {
        await uploadToS3(saveAsDraft)
      } else {
        await dispatch(
          sendPrivateOffer({
            partnerId: routeAccountId || '',
            productId,
            saveAsDraft,
            crmOpportunityId,
            crmObjectLink,
          })
        )
      }
    },
    [
      privateOffer,
      product,
      productId,
      openModal,
      selectedEulaFiles,
      selectedCloudTypeProductListing,
      routeAccountId,
    ]
  )
  const handleOpenModal = useCallback(async () => {
    setOpenModal(true)
  }, [privateOffer, product, productId])

  const zeroDollarPricing = useMemo(
    () => getZeroDollarPricing(privateOffer?.dimensions || []),
    [privateOffer?.dimensions]
  )

  const handleClose = useCallback(() => {
    setOpenModal(false)
  }, [openModal])
  if (
    !isEmpty(routeAccountId) &&
    !isEmpty(privateOffer) &&
    !isEmpty(product) &&
    (privateOffer.draft ||
      privateOffer.offerStatus === OfferStatus.PRIVATE_OFFER_IN_DRAFT)
  ) {
    if (selectedCloudTypeProductListing === 'GCP') {
      return (
        <PrivateOfferAddEditFormik
          product={product}
          externalBaseURL={externalBaseURL}
          crmOpportunityId={crmOpportunityId}
          crmObjectLink={crmObjectLink}
        />
      )
    } else {
      return (
        <div className={classes.root}>
          <div className={classes.stepperWrapper} data-testid="stepperDiv">
            <StepperComponent
              activeStep={activeStep}
              steps={stepsForPrivateOffer}
            />
            <PrivateOfferCreation
              selectedEulaFiles={selectedEulaFiles}
              setEulaFile={setEulaFile}
              disableUpload={disableUpload}
              handleChange={handleChange}
              privateOffer={privateOffer}
              addRow={addRow}
              updateValuesInArray={updateValuesInArray}
              deleteRow={deleteRow}
              product={product}
              updateStandardDimensions={updateStandardDimensions}
            />
            <div className={classes.buttonContainer}>
              <ButtonV2
                styleType="outlined"
                onClick={handleOpenModal}
                disabled={loading || draftDisabled}
                className={classes.saveButton}
              >
                {'Save and preview'}
              </ButtonV2>
              <ButtonV2
                styleType="primary"
                onClick={handleSentToBuyer(false)}
                disabled={sendDisabled || loading}
              >
                {'Send to buyer'}
              </ButtonV2>
            </div>
          </div>
          <ModalConfirm
            title={'Preview'}
            subTitle={''}
            content={
              <DetailedOffer
                offer={
                  {
                    ...privateOffer,
                    zeroDollarPricing: zeroDollarPricing,
                    offerName: privateOffer.privateOfferName,
                  } as unknown as DetailedOffer
                }
                planType={product.productType}
              />
            }
            open={openModal}
            titleAcceptButton={'Okay'}
            onAccept={handleSentToBuyer(true)}
            onDecline={handleClose}
            variant={ModalSizeVariants.medium}
            hideCancelButton={true}
          />
        </div>
      )
    }
  } else {
    return <div data-testid="empty-div-private-offer-creation"></div>
  }
}
